import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { MenuItem, MessageService } from 'primeng/api';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Breadcrumb } from 'primeng/breadcrumb';
import { Dialog } from 'primeng/dialog';
import { Checkbox } from 'primeng/checkbox';
import { UsersService } from '../services/users.service';
import {
  RoleOption,
  StatusOption,
  UserDTO,
  UserGetDTO,
  UserStatus,
  UserUpdateDTO,
} from '../interfaces/users-interface';
import { UsersTableComponent } from '../components/tables/user-table.component';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { BreadcrumbNavigationService } from '@angular-monorepo/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { animate, style, transition, trigger } from '@angular/animations';
import { MultiSelect } from 'primeng/multiselect';

@Component({
  selector: 'lib-users',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    InputText,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    Breadcrumb,
    Dialog,
    UsersTableComponent,
    Checkbox,
    TranslatePipe,
    InputGroup,
    InputGroupAddon,
    MultiSelect,
  ],
  providers: [MessageService],
  templateUrl: './users.component.html',
  styleUrl: './users.component.css',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
    trigger('smoothResize', [
      transition(
        '* <=> *',
        [
          style({ height: '{{startHeight}}px' }),
          animate('300ms ease-in-out', style({ height: '*' })),
        ],
        { params: { startHeight: 0 } },
      ),
    ]),
  ],
})
export class UsersComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UsersService);
  private readonly translate = inject(TranslateService);

  searchTerm = '';
  rows = 10;
  first = 0;
  loading = false;
  totalRecords = 0;
  showCreateUserDialog = false;
  showEditUserDialog = false;
  userForm!: FormGroup;
  editUserForm!: FormGroup;
  isSubmitting = false;
  isEditingSubmitting = false;
  selectedStatus: UserStatus = 'all';
  currentEditingUser: UserGetDTO | null = null;
  userToDelete: UserGetDTO | null = null;
  showDeleteConfirmDialog = false;
  @Input() usersBreadcrumbs = signal<MenuItem[]>([]);
  @Input() breadcrumbUserRoute = 'users';
  @Input() useUserDynamicBreadcrumbs = true;

  // Opciones del filtro de status
  statusOptions: StatusOption[] = [
    {
      label: '',
      value: 'all',
      icon: 'pi pi-list',
    },
    {
      label: '',
      value: 'active',
      icon: 'pi pi-check-circle',
    },
    {
      label: '',
      value: 'inactive',
      icon: 'pi pi-times-circle',
    },
  ];
  roles: RoleOption[] = [];
  loadingRoles = false;
  private usersService = inject(UsersService);
  users$ = this.usersService.users$;
  loading$ = this.usersService.loading$;
  private messageService = inject(MessageService);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private statusSubject = new Subject<UserStatus>();
  private breadcrumbNavigationService = inject(BreadcrumbNavigationService);
  public displayUserBreadcrumbs = computed(() => {
    if (this.useUserDynamicBreadcrumbs) {
      return this.breadcrumbNavigationService.generateBreadcrumbs(this.breadcrumbUserRoute);
    }
    return this.usersBreadcrumbs();
  });

  constructor() {
    this.initializeForm();
  }

  ngOnInit() {
    this.initializeStatusOptions();
    this.setupSearchSubscription();
    this.setupStatusSubscription();
    this.loadRoles();

    this.loading$?.subscribe((value) => (this.loading = value));

    this.loadUsers(1, this.searchTerm, this.selectedStatus);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Métodos auxiliares for template
  getSelectedStatusIcon(): string {
    const option = this.statusOptions.find((opt) => opt.value === this.selectedStatus);
    return option?.icon || 'pi pi-list';
  }

  getSelectedStatusLabel(): string {
    const option = this.statusOptions.find((opt) => opt.value === this.selectedStatus);
    return option?.label || this.translate.instant('usersModule.status');
  }

  private initializeStatusOptions(): void {
    this.statusOptions = [
      { label: this.translate.instant('usersModule.status'), value: 'all', icon: 'pi pi-list' },
      {
        label: this.translate.instant('usersModule.active'),
        value: 'active',
        icon: 'pi pi-check-circle',
      },
      {
        label: this.translate.instant('usersModule.inactive'),
        value: 'inactive',
        icon: 'pi pi-times-circle',
      },
    ];
  }

  onSearchInput() {
    this.searchSubject.next(this.searchTerm);
  }

  onSearchClear() {
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  onStatusFilterChange() {
    this.statusSubject.next(this.selectedStatus);
  }

  onTableSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onTableStatusChange(value: UserStatus): void {
    this.selectedStatus = value;
    this.statusSubject.next(value);
  }

  onTableClearFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = 'all';
    this.first = 0;
    this.loadUsers(1, '', 'all');
  }

  showCreateUserModal() {
    this.showCreateUserDialog = true;
    this.userForm.reset();
  }

  hideCreateUserModal() {
    this.showCreateUserDialog = false;
    this.userForm.reset();
    this.isSubmitting = false;
  }

  showEditUserModal() {
    this.showEditUserDialog = true;
  }

  togglePasswordVisibility(input: HTMLInputElement): void {
    input.type = input.type === 'password' ? 'text' : 'password';
  }

  hideEditUserModal() {
    this.showEditUserDialog = false;
    this.editUserForm.reset({
      email: '',
      name: '',
      roles: [],
      password: '',
      is_active: true,
      is_deleted: false,
    });
    this.isEditingSubmitting = false;
    this.currentEditingUser = null;
  }

  onCreateUser() {
    if (this.userForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const userData: UserDTO = this.userForm.value;

      this.usersService.createUser(userData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Usuario creado correctamente',
          });
          this.hideCreateUserModal();
          this.loadUsers(this.first / this.rows + 1, this.searchTerm, this.selectedStatus);
        },
        error: (error) => {
          let errorMessage = 'Error al crear usuario';

          if (error.error?.message) {
            errorMessage += `: ${error.error.message}`;
          } else if (error.error?.email) {
            errorMessage += ': El email ya está en uso';
          } else if (error.error?.name) {
            errorMessage += ': El nombre de usuario ya está en uso';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
          this.isSubmitting = false;
        },
      });
    } else {
      this.markFormGroupTouched(this.userForm);
    }
  }

  onUpdateUser() {
    if (this.editUserForm.valid && !this.isEditingSubmitting && this.currentEditingUser) {
      this.isEditingSubmitting = true;
      const formData = this.editUserForm.value;

      // Preparar datos para actualización - solo incluir campos que han cambiado
      const updateData: Partial<UserUpdateDTO> = {
        email: formData.email,
      };

      if (formData.name?.trim()) {
        updateData.name = formData.name.trim();
      }

      if (formData.roles?.length) {
        updateData.role = formData.roles;
      }

      if (formData.password?.trim()) {
        updateData.password = formData.password;
      }

      updateData.is_active = formData.is_active;
      updateData.is_deleted = formData.is_deleted;

      this.usersService.updateUser(this.currentEditingUser.id, updateData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Usuario actualizado correctamente',
          });
          this.hideEditUserModal();
          this.loadUsers(this.first / this.rows + 1, this.searchTerm, this.selectedStatus);
        },
        error: (error) => {
          let errorMessage = 'Error al actualizar usuario';

          if (error.error?.message) {
            errorMessage += `: ${error.error.message}`;
          } else if (error.error?.email) {
            errorMessage += ': El email ya está en uso';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
          this.isEditingSubmitting = false;
        },
      });
    } else {
      this.markFormGroupTouched(this.editUserForm);
    }
  }

  // Método sobrecargado para manejar ambos formularios
  isFieldInvalid(fieldName: string): boolean;

  isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean;

  isFieldInvalid(formGroupOrFieldName: FormGroup | string, fieldName?: string): boolean {
    if (typeof formGroupOrFieldName === 'string') {
      const field = this.userForm.get(formGroupOrFieldName);
      return !!(field && field.invalid && (field.dirty || field.touched));
    } else {
      // Caso para cualquier formulario especificado
      if (!fieldName) {
        return false;
      }
      const field = formGroupOrFieldName.get(fieldName);
      return !!(field && field.invalid && (field.dirty || field.touched));
    }
  }

  // Método sobrecargado para manejar ambos formularios
  getFieldError(fieldName: string): string;

  getFieldError(formGroup: FormGroup, fieldName: string): string;

  getFieldError(formGroupOrFieldName: FormGroup | string, fieldName?: string): string {
    let field;
    let actualFieldName: string;

    if (typeof formGroupOrFieldName === 'string') {
      field = this.userForm.get(formGroupOrFieldName);
      actualFieldName = formGroupOrFieldName;
    } else {
      if (!fieldName) {
        return '';
      }
      // Caso para cualquier formulario especificado
      field = formGroupOrFieldName.get(fieldName);
      actualFieldName = fieldName;
    }

    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(actualFieldName)} es requerido`;
      }
      if (field.errors['email']) {
        return 'Email no válido';
      }
      if (field.errors['minlength']) {
        const requiredLength = field.errors['minlength'].requiredLength;
        return `${this.getFieldLabel(
          actualFieldName,
        )} debe tener al menos ${requiredLength} caracteres`;
      }
    }
    return '';
  }

  loadUsers(page: number, search?: string, status?: UserStatus) {
    const currentPage = page ?? this.first / this.rows + 1;
    this.usersService.getUsers(currentPage, this.rows, search, status).subscribe({
      next: (response) => {
        this.totalRecords = response.total ?? 0;
        if (response.data?.length) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Usuarios cargados correctamente',
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar usuarios',
        });
      },
    });
  }

  onEditUser(user: UserGetDTO) {
    this.currentEditingUser = user;

    this.editUserForm.patchValue({
      email: user.email,
      name: user.name || '',
      roles: this.normalizeRolesValue(user.role),
      password: '',
      is_active: user.is_active,
      is_deleted: user.is_deleted,
    });

    this.showEditUserModal();
  }

  onDeleteUser(user: UserGetDTO) {
    this.confirmDelete(user);
  }

  // Método para abrir el diálogo de confirmación
  confirmDelete(user: UserGetDTO) {
    this.userToDelete = user;
    this.showDeleteConfirmDialog = true;
  }

  onConfirmDelete() {
    if (!this.userToDelete) return;

    this.usersService.deleteUser(this.userToDelete.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'warn',
          summary: 'usuario eliminado',
          detail: `${this.userToDelete?.name} fue eliminado exitosamente`,
        });
        this.userToDelete = null;
        this.showDeleteConfirmDialog = false;
        this.loadUsers(this.first / this.rows + 1, this.searchTerm, this.selectedStatus);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo eliminar el usuario',
        });
        this.userToDelete = null;
        this.showDeleteConfirmDialog = false;
      },
    });
  }

  onToggleStatus(event: { user: UserGetDTO; status: boolean }) {
    const { user, status } = event;
    const payload: Partial<UserUpdateDTO> = { is_active: status };
    this.usersService.updateUser(event.user.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: status ? 'success' : 'warn',
          summary: status ? 'Usuario activado' : 'Usuario desactivado',
          detail: `${user.name} fue ${status ? 'activado' : 'desactivado'} exitosamente`,
        });
        this.loadUsers(this.first / this.rows + 1, this.searchTerm, this.selectedStatus);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo actualizar el estado',
        });
      },
    });
  }

  onPageChange(event: { page: number; rows: number }) {
    if (this.rows !== event.rows || this.first !== (event.page - 1) * event.rows) {
      this.rows = event.rows;
      this.first = (event.page - 1) * event.rows;
      this.loadUsers(event.page, this.searchTerm, this.selectedStatus);
    }
  }

  // Métodos auxiliares para validación del formulario de edición
  isEditFieldInvalid(fieldName: string): boolean {
    const field = this.editUserForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getEditFieldError(fieldName: string): string {
    const field = this.editUserForm.get(fieldName);
    return this.getErrorMessage(field);
  }

  private loadRoles(): void {
    this.loadingRoles = true;

    this.userService.getRoles().subscribe({
      next: (roles: RoleOption[]) => {
        this.roles = roles;
        this.loadingRoles = false;
      },
      error: (error) => {
        console.error('Error cargando roles:', error);
        this.loadingRoles = false;
      },
    });
  }

  private initializeForm(): void {
    this.userForm = this.fb.group({
      name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        this.passwordValidator,
      ]),
    });

    this.editUserForm = this.fb.group({
      email: new FormControl('', [Validators.required, Validators.email]),
      name: new FormControl(''),
      roles: new FormControl<string[]>([], Validators.required),
      password: new FormControl('', [this.passwordValidator]),
      is_active: new FormControl(true),
      is_deleted: new FormControl(false),
    });
  }

  private normalizeRolesValue(role: UserGetDTO['role']): string[] {
    if (!role) {
      return [];
    }

    if (Array.isArray(role)) {
      return role
        .map((item) => {
          if (typeof item === 'string') {
            return item.trim();
          }

          if (item && typeof item === 'object' && 'name' in item) {
            return String(item.name ?? '').trim();
          }

          return '';
        })
        .filter(Boolean);
    }

    if (typeof role === 'string') {
      const normalizedRole = role.trim();
      return normalizedRole ? [normalizedRole] : [];
    }

    if (typeof role === 'object' && 'name' in role) {
      const normalizedRole = String(role.name ?? '').trim();
      return normalizedRole ? [normalizedRole] : [];
    }

    return [];
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      name: 'Nombre',
      email: 'Email',
      password: 'Contraseña',
      role: 'Rol',
      roles: 'Roles',
    };
    return labels[fieldName] || fieldName;
  }

  private setupSearchSubscription() {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.loadUsers(1, searchTerm, this.selectedStatus);
      });
  }

  private setupStatusSubscription() {
    this.statusSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((status) => {
        this.loadUsers(1, this.searchTerm, status);
      });
  }

  private getErrorMessage(field: AbstractControl | null): string {
    if (!field || !field.errors) return '';

    const errors = field.errors;
    if (errors['required']) return this.translate.instant('auth.errors.required');
    if (errors['email']) return this.translate.instant('auth.errors.email');
    if (errors['minlength']) {
      return this.translate.instant('auth.errors.minlength', {
        requiredLength: errors['minlength'].requiredLength,
      });
    }
    if (errors['maxlength']) {
      return this.translate.instant('auth.errors.maxlength', {
        requiredLength: errors['maxlength'].requiredLength,
      });
    }
    if (errors['uppercase']) return this.translate.instant('usersModule.errors.password.uppercase');
    if (errors['lowercase']) return this.translate.instant('usersModule.errors.password.lowercase');
    if (errors['characters'])
      return this.translate.instant('usersModule.errors.password.characters');
    if (errors['special']) return this.translate.instant('usersModule.errors.password.special');

    return this.translate.instant('Campo inválido');
  }

  private passwordValidator(control: AbstractControl) {
    const value = control.value || '';

    // Si el valor está vacío, no validamos (especialmente para formulario de edición)
    if (!value || value.trim().length === 0) {
      return null;
    }

    const errors: Record<string, boolean> = {};

    if (!/[A-Z]/.test(value)) {
      errors['uppercase'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['lowercase'] = true;
    }
    if (value.length < 10) {
      errors['characters'] = true;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      errors['special'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }
}
